'use client'

import { IProductVariant } from '@/lib/types/core/product-variant'
import { Modal } from '@heroui/react'
import { Input } from '@/components/ui/form-elements/Input'
import { useMemo, useState } from 'react'

import { extractColorName, extractOptionLabel, isWidthAxis, resolveColorHex, swatchMediaUrl } from './color-swatch-rail'

// --- Types ---

export interface AllColorsModalProps {
  isOpen: boolean
  onClose: () => void
  variants: IProductVariant[]
  selectedVariantId: string | null
  variantInventory: Array<{ variantId: string; inventory: number; inStock: boolean }>
  onSelectColor: (variantId: string) => void
}

// --- Helpers ---

type StockTier = 'in-stock' | 'low-stock' | 'out-of-stock'

const LOW_STOCK_THRESHOLD = 10

function getStockTier(
  variantId: string,
  inventory: AllColorsModalProps['variantInventory']
): StockTier {
  const entry = inventory.find((v) => v.variantId === variantId)
  if (!entry || !entry.inStock) return 'out-of-stock'
  if (entry.inventory <= LOW_STOCK_THRESHOLD) return 'low-stock'
  return 'in-stock'
}

const TIER_ORDER: Record<StockTier, number> = {
  'in-stock': 0,
  'low-stock': 1,
  'out-of-stock': 2
}

// --- Component ---

export function AllColorsModal({
  isOpen,
  onClose,
  variants,
  selectedVariantId,
  variantInventory,
  onSelectColor
}: AllColorsModalProps) {
  const [search, setSearch] = useState('')
  const widthAxis = isWidthAxis(variants)
  const noun = widthAxis ? 'width' : 'color'

  // Out-of-stock colours/widths aren't offered at all, not shown-but-disabled.
  const inStockVariants = useMemo(
    () => variants.filter((v) => getStockTier(v.id, variantInventory) !== 'out-of-stock'),
    [variants, variantInventory]
  )

  // Sort: in-stock first, low-stock second
  const sortedVariants = useMemo(() => {
    return [...inStockVariants].sort((a, b) => {
      const tierA = TIER_ORDER[getStockTier(a.id, variantInventory)]
      const tierB = TIER_ORDER[getStockTier(b.id, variantInventory)]
      return tierA - tierB
    })
  }, [inStockVariants, variantInventory])

  // Filter by search query
  const filteredVariants = useMemo(() => {
    if (!search.trim()) return sortedVariants
    const q = search.trim().toLowerCase()
    return sortedVariants.filter((v) =>
      (extractOptionLabel(v, widthAxis) + ' ' + (v.name ?? '')).toLowerCase().includes(q)
    )
  }, [sortedVariants, search, widthAxis])

  const handleSelect = (variantId: string) => {
    const tier = getStockTier(variantId, variantInventory)
    if (tier === 'out-of-stock') return
    onSelectColor(variantId)
    onClose()
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      setSearch('')
    }
  }

  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Container size='lg'>
        <Modal.Dialog>
          <Modal.Header className='flex flex-col gap-2'>
            <h2 className='text-lg font-semibold text-foreground'>Choose a {noun}</h2>
            <Input
              placeholder={`Search ${noun}s...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={`Search ${noun}s`}
              className='w-full'
              isClearable
              onClear={() => setSearch('')}
            />
          </Modal.Header>

          <Modal.Body className='pb-6'>
            {filteredVariants.length === 0 ? (
              <p className='text-center text-muted py-8'>
                No {noun}s match &apos;{search}&apos;
              </p>
            ) : (
              <div className='grid grid-cols-4 sm:grid-cols-6 gap-3'>
                {filteredVariants.map((variant) => {
                  const colorName = extractOptionLabel(variant, widthAxis)
                  const hex = variant.swatchHex ?? resolveColorHex(extractColorName(variant))
                  const mediaUrl = swatchMediaUrl(variant)
                  const tier = getStockTier(variant.id, variantInventory)
                  const isSelected = variant.id === selectedVariantId
                  const isDisabled = tier === 'out-of-stock'
                  const inventoryEntry = variantInventory.find(
                    (v) => v.variantId === variant.id
                  )

                  return (
                    <button
                      key={variant.id}
                      type='button'
                      aria-label={`${colorName}${isDisabled ? ' (out of stock)' : ''}`}
                      aria-disabled={isDisabled}
                      disabled={isDisabled}
                      onClick={() => handleSelect(variant.id)}
                      className={`
                        flex flex-col items-center gap-1.5 rounded-lg p-2 transition-colors
                        ${isDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-surface'}
                        ${isSelected ? 'ring-2 ring-accent rounded-lg' : ''}
                      `}
                    >
                      {/* Swatch (colour axis) or width chip */}
                      <div className='relative'>
                        {widthAxis ? (
                          <div
                            className={`
                              flex h-12 min-w-12 items-center justify-center rounded-lg border border-divider px-2 text-sm font-semibold
                              ${isDisabled ? 'text-muted' : 'text-foreground'}
                            `}
                          >
                            {colorName}
                          </div>
                        ) : (
                          <div
                            className={`
                              h-12 w-12 overflow-hidden rounded-full border border-divider
                              ${isDisabled ? 'grayscale' : ''}
                            `}
                            style={{ backgroundColor: hex }}
                          >
                            {mediaUrl && (
                               
                              <img
                                src={mediaUrl}
                                alt=''
                                aria-hidden='true'
                                className='h-full w-full object-cover'
                              />
                            )}
                          </div>
                        )}
                        {/* Low stock badge */}
                        {tier === 'low-stock' && inventoryEntry && (
                          <span className='absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-warning px-1 text-[10px] font-bold text-foreground'>
                            {inventoryEntry.inventory}
                          </span>
                        )}
                      </div>

                      {/* Label */}
                      <span
                        className={`
                          text-xs text-center leading-tight line-clamp-2
                          ${isDisabled ? 'text-muted' : 'text-foreground'}
                        `}
                      >
                        {widthAxis ? variant.name : colorName}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}
